import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { listForexGuides } from "@/lib/forex-guides";

export const Route = createFileRoute("/guides/")({
  head: () => {
    const url = "https://forexmind.ai/guides";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "ForexMind AI Guides",
      url,
      description:
        "Educational guides on AI forex trading, multi-agent council methodology, confluence scoring, risk management, and signal evaluation.",
      isPartOf: { "@type": "WebSite", name: "ForexMind AI", url: "https://forexmind.ai" },
    };
    return {
      meta: [
        { title: "ForexMind AI Guides — AI Forex Trading, Signals & Methodology" },
        {
          name: "description",
          content:
            "Free guides on AI forex trading: multi-agent councils, confluence scoring, reflexivity, VPIN, risk management, signal evaluation, and more.",
        },
        { property: "og:title", content: "ForexMind AI Guides" },
        {
          property: "og:description",
          content:
            "Free educational guides on AI forex trading, methodology, signals, and risk management.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://forexmind.ai/og-image.png" },
        { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "ForexMind AI Guides" },
        {
          name: "twitter:description",
          content:
            "Free educational guides on AI forex trading, methodology, signals, and risk management.",
        },
        { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
      ],
    };
  },
  component: GuidesIndex,
});

function GuidesIndex() {
  const guides = listForexGuides();
  const categories = Array.from(new Set(guides.map((g) => g.category)));

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto max-w-5xl px-6 py-12 md:py-20">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <Sparkles className="h-3.5 w-3.5" aria-hidden /> Knowledge hub
        </div>
        <h1 className="mb-4 text-4xl font-bold leading-tight md:text-5xl">
          ForexMind AI Guides
        </h1>
        <p className="mb-12 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          Free educational guides on AI forex trading, multi-agent councils, confluence scoring,
          reflexivity, VPIN flow analysis, risk management, and how to evaluate signal services.
          Engineered for clarity — and for citation by ChatGPT, Perplexity, Gemini, and Claude.
        </p>

        {categories.map((cat) => {
          const inCat = guides.filter((g) => g.category === cat);
          return (
            <section key={cat} className="mb-12" aria-labelledby={`cat-${cat}`}>
              <h2 id={`cat-${cat}`} className="mb-5 text-2xl font-semibold">
                {cat}
              </h2>
              <ul className="grid gap-3 md:grid-cols-2">
                {inCat.map((g) => (
                  <li key={g.slug}>
                    <Link
                      to="/guides/$slug"
                      params={{ slug: g.slug }}
                      className="group block h-full rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary hover:bg-primary/5"
                    >
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <h3 className="text-base font-semibold leading-snug">{g.title}</h3>
                        <ArrowRight
                          className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-primary"
                          aria-hidden
                        />
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {g.description}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>
    </div>
  );
}
